import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Upload,
  Library,
  ArrowRight,
  Plus,
  BookOpen,
  Presentation,
  Folder,
  Search,
  Filter,
  Grid3X3,
  List,
  LayoutTemplate,
  Menu,
  X,
  Users,
  UserCircle,
  Store,
  MapPin,
} from "lucide-react";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import ScopingAISidebar from "../components/ScopingAISidebar";
// import { OnboardingWrapper } from "../components/onboarding/OnboardingWrapper";

export default function ResourceLibraryPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleNavigateToTemplates = () => {
    navigate("/tender/templates");
  };

  const handleNavigateToProposalTemplates = () => {
    navigate("/tender/resource-library/documents");
  };

  const handleNavigateToNewDocument = () => {
    navigate("/tender/documents/new");
  };

  return (
    // <OnboardingWrapper>
    <div className="min-h-screen bg-gray-50">
        {/* Sidebar Overlay for Mobile */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
            <div className="fixed left-0 top-0 h-full w-64 bg-white shadow-xl">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="font-semibold">Navigation</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <ScopingAISidebar />
            </div>
          </div>
        )}

        {/* Desktop Sidebar */}
        <div className="hidden lg:fixed lg:left-0 lg:top-16 lg:h-[calc(100vh-4rem)] lg:w-64 lg:block lg:z-40">
          <ScopingAISidebar />
        </div>
        {/* Main Content Area - Full Width */}
        <div className="lg:ml-64 w-full lg:w-[calc(100%-16rem)]">
          {/* Mobile Header with Menu Button */}
          <div className="lg:hidden bg-white border-b border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold">Resource Library</h1>
              <div></div>
            </div>
          </div>

          {/* Full Width Container */}
          <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 to-white">
            {/* Header Section - Full Width */}
            <div className="w-full bg-white border-b border-gray-200 shadow-sm">
              <div className="w-full px-6 py-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/10 p-4 rounded-2xl">
                      <BookOpen className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <h1 className="text-4xl font-bold text-gray-900 mb-2">
                        Resource Library
                      </h1>
                      <p className="text-lg text-gray-600">
                        Manage templates and proposal resources with world-class experience
                      </p>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex items-center gap-4">
                    <Button
                      onClick={handleNavigateToNewDocument}
                      className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      New Proposal
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filter Bar */}
            <div className="w-full px-6 py-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4 flex-1 max-w-2xl">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      placeholder="Search templates and resources..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-12 pr-4 py-3 text-base border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="px-4 py-3 border-gray-200 rounded-xl hover:bg-gray-50"
                  >
                    <Filter className="w-5 h-5 mr-2" />
                    Filter
                  </Button>
                </div>

                <div className="flex items-center gap-2 ml-6">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    className="px-3 py-2"
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    className="px-3 py-2"
                  >
                    <List className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Main Content - Templates and Proposal Templates */}
            <div className="w-full px-4 md:px-6 pb-12">
              <div className={`grid gap-4 md:gap-6 lg:gap-8 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>

            {/* Templates Section */}
            <Card className="group relative overflow-hidden border border-gray-200 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 hover:scale-[1.02] transform-gpu rounded-2xl bg-white h-80">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <CardHeader className="relative z-10 p-8 pb-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-blue-100 p-4 rounded-2xl group-hover:bg-blue-200 transition-all duration-300 group-hover:scale-110">
                    <LayoutTemplate className="h-8 w-8 text-blue-600 transition-transform duration-300 group-hover:rotate-12" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                      Templates
                    </CardTitle>
                    <Badge variant="secondary" className="mt-2 bg-blue-100 text-blue-700">
                      Document Templates
                    </Badge>
                  </div>
                </div>

                <CardDescription className="text-base text-gray-600 leading-relaxed">
                  Professional document templates for proposals and contracts. Streamline your workflow with customizable templates.
                </CardDescription>
              </CardHeader>

              <CardContent className="relative z-10 px-8 pt-0 pb-8">
                <Button
                  onClick={handleNavigateToTemplates}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-medium py-4 rounded-xl"
                >
                  <Library className="w-5 h-5 mr-2" />
                  Manage Templates
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </CardContent>
            </Card>

       

            {/* Clients Section */}
            <Card className="group relative overflow-hidden border border-gray-200 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 hover:scale-[1.02] transform-gpu rounded-2xl bg-white h-80">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <CardHeader className="relative z-10 p-8 pb-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-purple-100 p-4 rounded-2xl group-hover:bg-purple-200 transition-all duration-300 group-hover:scale-110">
                    <Users className="h-8 w-8 text-purple-600 transition-transform duration-300 group-hover:rotate-12" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors duration-300">
                      Clients
                    </CardTitle>
                    <Badge variant="secondary" className="mt-2 bg-purple-100 text-purple-700">
                      Client Management
                    </Badge>
                  </div>
                </div>

                <CardDescription className="text-base text-gray-600 leading-relaxed">
                  Manage your client organizations and their information. Keep track of all your clients in one place.
                </CardDescription>
              </CardHeader>

              <CardContent className="relative z-10 px-8 pt-0 pb-8">
                <Button
                  onClick={() => navigate("/tender/resource-library/clients")}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-medium py-4 rounded-xl"
                >
                  <Users className="w-5 h-5 mr-2" />
                  Manage Clients
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </CardContent>
            </Card>

            {/* Contacts Section */}
            <Card className="group relative overflow-hidden border border-gray-200 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 hover:scale-[1.02] transform-gpu rounded-2xl bg-white h-80">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <CardHeader className="relative z-10 p-8 pb-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-orange-100 p-4 rounded-2xl group-hover:bg-orange-200 transition-all duration-300 group-hover:scale-110">
                    <UserCircle className="h-8 w-8 text-orange-600 transition-transform duration-300 group-hover:rotate-12" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-300">
                      Contacts
                    </CardTitle>
                    <Badge variant="secondary" className="mt-2 bg-orange-100 text-orange-700">
                      Contact Management
                    </Badge>
                  </div>
                </div>

                <CardDescription className="text-base text-gray-600 leading-relaxed">
                  Manage contact persons for your clients. Keep all your client contacts organized and accessible.
                </CardDescription>
              </CardHeader>

              <CardContent className="relative z-10 px-8 pt-0 pb-8">
                <Button
                  onClick={() => navigate("/tender/resource-library/contacts")}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-medium py-4 rounded-xl"
                >
                  <UserCircle className="w-5 h-5 mr-2" />
                  Manage Contacts
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </CardContent>
            </Card>

            {/* Retailers Section */}
            <Card className="group relative overflow-hidden border border-gray-200 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 hover:scale-[1.02] transform-gpu rounded-2xl bg-white h-80">
              <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <CardHeader className="relative z-10 p-8 pb-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-red-100 p-4 rounded-2xl group-hover:bg-red-200 transition-all duration-300 group-hover:scale-110">
                    <Store className="h-8 w-8 text-red-600 transition-transform duration-300 group-hover:rotate-12" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-red-600 transition-colors duration-300">
                      Retailers
                    </CardTitle>
                    <Badge variant="secondary" className="mt-2 bg-red-100 text-red-700">
                      Energy Providers
                    </Badge>
                  </div>
                </div>

                <CardDescription className="text-base text-gray-600 leading-relaxed">
                  Manage energy retailers and providers. Keep track of all energy suppliers for tender offers.
                </CardDescription>
              </CardHeader>

              <CardContent className="relative z-10 px-8 pt-0 pb-8">
                <Button
                  onClick={() => navigate("/tender/resource-library/retailers")}
                  className="w-full bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-medium py-4 rounded-xl"
                >
                  <Store className="w-5 h-5 mr-2" />
                  Manage Retailers
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </CardContent>
            </Card>

            {/* Sites Section */}
            <Card className="group relative overflow-hidden border border-gray-200 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 hover:scale-[1.02] transform-gpu rounded-2xl bg-white h-80">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <CardHeader className="relative z-10 p-8 pb-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-teal-100 p-4 rounded-2xl group-hover:bg-teal-200 transition-all duration-300 group-hover:scale-110">
                    <MapPin className="h-8 w-8 text-teal-600 transition-transform duration-300 group-hover:rotate-12" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors duration-300">
                      Sites
                    </CardTitle>
                    <Badge variant="secondary" className="mt-2 bg-teal-100 text-teal-700">
                      Energy Sites
                    </Badge>
                  </div>
                </div>

                <CardDescription className="text-base text-gray-600 leading-relaxed">
                  Manage energy consumption sites. Track all client locations and their energy usage data.
                </CardDescription>
              </CardHeader>

              <CardContent className="relative z-10 px-8 pt-0 pb-8">
                <Button
                  onClick={() => navigate("/tender/resource-library/sites")}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-medium py-4 rounded-xl"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  Manage Sites
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </CardContent>
            </Card>

            {/* LOAs Section */}
            {/* <Card className="group relative overflow-hidden border border-gray-200 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 hover:scale-[1.02] transform-gpu rounded-2xl bg-white">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <CardHeader className="relative z-10 p-8 pb-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-indigo-100 p-4 rounded-2xl group-hover:bg-indigo-200 transition-all duration-300 group-hover:scale-110">
                      <FileCheck className="h-8 w-8 text-indigo-600 transition-transform duration-300 group-hover:rotate-12" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors duration-300">
                        Letters of Authority
                      </CardTitle>
                      <Badge variant="secondary" className="mt-2 bg-indigo-100 text-indigo-700">
                        LOA Documents
                      </Badge>
                    </div>
                  </div>
                </div>

                <CardDescription className="text-base text-gray-600 leading-relaxed">
                  Manage LOA documents for energy switching. Upload and track all Letters of Authority.
                </CardDescription>
              </CardHeader>

              <CardContent className="relative z-10 px-8 pt-0 pb-8">
                <Button
                  onClick={() => navigate("/tender/resource-library/loas")}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-medium py-4 rounded-xl"
                >
                  <FileCheck className="w-5 h-5 mr-2" />
                  Manage LOAs
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </CardContent>
            </Card> */}

            {/* Tenders Section */}
            <Card className="group relative overflow-hidden border border-gray-200 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 hover:scale-[1.02] transform-gpu rounded-2xl bg-white h-80">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <CardHeader className="relative z-10 p-8 pb-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-amber-100 p-4 rounded-2xl group-hover:bg-amber-200 transition-all duration-300 group-hover:scale-110">
                    <FileText className="h-8 w-8 text-amber-600 transition-transform duration-300 group-hover:rotate-12" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-amber-600 transition-colors duration-300">
                      Tenders
                    </CardTitle>
                    <Badge variant="secondary" className="mt-2 bg-amber-100 text-amber-700">
                      Tender Management
                    </Badge>
                  </div>
                </div>

                <CardDescription className="text-base text-gray-600 leading-relaxed">
                  Manage all energy procurement tenders. View, filter, and track tender opportunities.
                </CardDescription>
              </CardHeader>

              <CardContent className="relative z-10 px-8 pt-0 pb-8">
                <Button
                  onClick={() => navigate("/tender/resource-library/tenders")}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-medium py-4 rounded-xl"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Manage Tenders
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </CardContent>
            </Card>
          </div>
              </div>

              {/* Footer Section */}
              {/* <div className="w-full px-6 py-8 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    <p>Manage your templates and proposal resources efficiently</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Button variant="outline" size="sm" className="text-gray-600">
                      <Upload className="w-4 h-4 mr-2" />
                      Import Template
                    </Button>
                    <Button variant="outline" size="sm" className="text-gray-600">
                      <Folder className="w-4 h-4 mr-2" />
                      Export All
                    </Button>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
    // </OnboardingWrapper>
  );
}
